<template>
  <o-modal
    ref="modal"
    :title="isOwner ? $t('changeEmail.details') : $t('changeEmail.title')"
    :info="isOwner ? $t('changeEmail.detailsInfo') : $t('components.adminUser.actions.changeEmail.info')"
    :loading="loading"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-md relative"
    content-class="mx-auto max-w-lg h-full flex flex-col"
  >
    <o-form ref="form" class="text-left h-full flex flex-col justify-between">
      <o-scroll v-if="adminUser" class="h-full">
        <div class="flex flex-col items-center justify-center mb-8">
          <o-avatar :items="[adminUser]" size="xl" class="mx-auto block" />
          <h2 class="inline mx-auto font-semibold text-gray-800 text-lg mt-2 dark:text-gray-100">
            {{ adminUser.displayName }}
          </h2>
        </div>

        <div class="mx-2 mb-3">
          <o-field-group
            :label="$t('general.email')"
            :name="$t('general.email')"
            :info="$t('components.adminUser.actions.changeEmail.newUserEmail')"
            rules="required|email"
            :validation-value="newEmail"
          >
            <text-field v-model="newEmail" type="email" />
          </o-field-group>
        </div>

        <template v-if="isOwner">
          <div class="mx-2 mb-3">
            <o-field-group :label="$t('general.firstName')" :name="$t('general.firstName')" rules="required" :validation-value="newFirstName">
              <text-field v-model="newFirstName" />
            </o-field-group>
          </div>

          <div class="mx-2 mb-3">
            <o-field-group :label="$t('general.lastName')" :name="$t('general.lastName')" rules="required" :validation-value="newLastName">
              <text-field v-model="newLastName" />
            </o-field-group>
          </div>
        </template>


        <div v-if="token" class="mx-2 mt-6">
          <o-field-group :label="$t('confirmation.confirmationCode')" :info="$t('confirmation.codeSentToEmail', { email })">
            <o-code v-model="code" />
          </o-field-group>

          <p class="text-blue-600 pt-2 text-sm text-right dark:text-blue-500">
            <a @click="handleRequestCode">{{ $t('confirmation.resendCode') }}</a>
          </p>
        </div>
      </o-scroll>

      <div class="mx-2 mt-12 pb-5">
        <o-button
          v-if="!token"
          :disabled="!newEmail"
          class="w-full"
          outline
          flat
          @click="handleRequestCode"
        >
          {{ $t('confirmation.requestCode') }}
        </o-button>

        <o-button v-if="token" :disabled="!code || code.length !== 6" class="w-full" variant="success" @click="handleConfirm">
          {{ $t('changeEmail.confirm') }}
        </o-button>
      </div>
    </o-form>
  </o-modal>
</template>

<script setup lang="ts">
import {
  OAvatar,
  OFieldGroup,
  OCode,
} from '@/components/elements'

import {
  TextField,
} from '@/components/fields'

interface Emits {
  (e: 'success', data: any): void
}

const emit = defineEmits<Emits>()
const { $api, $auth, $i18n, $notification } = useNuxtApp()

const modal = ref<any>()
const form = ref<any>()
const loading = ref(false)
const adminUser = ref<any>(null)
const newEmail = ref<string | null>(null)
const newFirstName = ref<string | null>(null)
const newLastName = ref<string | null>(null)
const email = ref<string | null>(null)
const token = ref<string | null>(null)
const code = ref<any>(false)

const isOwner = computed(() => adminUser.value?.role.isRoleTypeOwner)

watch(newEmail, () => {
  token.value = null
  code.value = null
})

watch(newFirstName, () => {
  token.value = null
  code.value = null
})

watch(newLastName, () => {
  token.value = null
  code.value = null
})

const open = (user: any) => {
  adminUser.value = user
  newEmail.value = user.email
  newFirstName.value = user.firstName
  newLastName.value = user.lastName

  modal.value.open()
}

const handleRequestCode = async () => {
  if (!await form.value?.validate()) return

  loading.value = true

  try {
    const response = await $api.adminUser.changeEmail({
      adminUser: adminUser.value,
      email: newEmail.value ?? undefined,
      firstName: newFirstName.value ?? undefined,
      lastName: newLastName.value ?? undefined,
    })

    if (response.data) {
      token.value = (response.data as any).token
      email.value = (response.data as any).email
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e.errors })
  }

  loading.value = false
}

const handleConfirm = async () => {
  if (!await form.value?.validate()) return

  loading.value = true

  try {
    const response = await $api.adminUser.changeEmail({
      adminUser: adminUser.value,
      token: token.value ?? undefined,
      code: code.value ?? undefined,
      email: newEmail.value ?? undefined,
      firstName: newFirstName.value ?? undefined,
      lastName: newLastName.value ?? undefined,
    })

    if (response.data) {
      $notification.toast({
        title: $i18n.t('changeEmail.success'),
        text: $i18n.t('components.adminUser.actions.changeEmail.successInfo'),
      })

      emit('success', response.data)

      modal.value.close()

      $auth.syncActive()
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e.errors })
  }

  loading.value = false
}

defineExpose({ open })
</script>
